import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MatrixFocusCell } from '../../../@types/service-activity.type';
import { getNextMatrixFocus } from '../../../utils/service-activity-view';

const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'] as const;
type ArrowKey = (typeof ARROW_KEYS)[number];

const isArrowKey = (key: string): key is ArrowKey =>
  (ARROW_KEYS as readonly string[]).includes(key);

const cellId = (rowKey: string, columnIndex: number): string => `${rowKey}:${columnIndex}`;

/**
 * Roving tabindex across the matrix body.
 *
 * The table keeps `role="table"` — `role="grid"` would break the e2e smoke
 * test, which locates the dashboard by `getByRole('table', { name: ... })`. So
 * the roving index lives on the buttons instead: the whole body is a single
 * Tab stop, and arrow keys move between cells. Enter and Space activate
 * natively because every target is a real `<button>`.
 */
export const useMatrixRovingFocus = (cells: readonly MatrixFocusCell[]) => {
  const refs = useRef(new Map<string, HTMLElement | null>());
  const [activeId, setActiveId] = useState<string | null>(null);

  const firstInteractive = useMemo(() => cells.find((cell) => cell.interactive) ?? null, [cells]);

  // Keep the active cell valid across a refetch. If the focused row vanished,
  // fall back to the first interactive cell rather than stranding focus on a
  // detached node — but never steal focus if the matrix never had it.
  useEffect(() => {
    if (!activeId) return;
    if (
      cells.some((cell) => cell.interactive && cellId(cell.rowKey, cell.columnIndex) === activeId)
    ) {
      return;
    }
    setActiveId(
      firstInteractive ? cellId(firstInteractive.rowKey, firstInteractive.columnIndex) : null
    );
  }, [cells, activeId, firstInteractive]);

  const registerRef = useCallback(
    (rowKey: string, columnIndex: number) => (node: HTMLElement | null) => {
      const id = cellId(rowKey, columnIndex);
      if (node) refs.current.set(id, node);
      else refs.current.delete(id);
    },
    []
  );

  /** Exactly one button in the body carries tabIndex 0. */
  const isActive = useCallback(
    (rowKey: string, columnIndex: number): boolean => {
      const id = cellId(rowKey, columnIndex);
      if (activeId) return activeId === id;
      return firstInteractive
        ? cellId(firstInteractive.rowKey, firstInteractive.columnIndex) === id
        : false;
    },
    [activeId, firstInteractive]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, current: MatrixFocusCell) => {
      if (!isArrowKey(event.key)) return;
      event.preventDefault();

      const next = getNextMatrixFocus(cells, current, event.key);
      const id = cellId(next.rowKey, next.columnIndex);
      setActiveId(id);
      refs.current.get(id)?.focus();
    },
    [cells]
  );

  return { registerRef, isActive, handleKeyDown };
};
